import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Check, Loader2, ArrowRight, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [orderNumber, setOrderNumber] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrderNumber(data.order_number);
          return;
        }
        if (tries >= 15) { setStatus("timeout"); return; }
        setTimeout(() => setTries((t) => t + 1), 2000);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, tries]);

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-20">
        {status === "polling" && (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-[color:var(--dms-peach)] mx-auto" />
            <h1 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-6">Confirming your payment…</h1>
            <p className="mt-3 text-[color:var(--dms-navy)]/60 text-sm">This usually takes just a second.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
              <Check className="w-8 h-8 text-[color:var(--dms-navy)]" />
            </div>
            <p className="kicker mt-6">Payment received</p>
            <h1 className="font-serif text-4xl text-[color:var(--dms-navy)] mt-3">Thank you!</h1>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
            <p className="mt-6 text-[color:var(--dms-navy)]/70">Order <span className="font-serif text-[color:var(--dms-navy)]">#{orderNumber}</span> is confirmed. A confirmation email is on its way.</p>
            <div className="mt-8 bg-[color:var(--dms-cream-2)] rounded-xl p-5 flex items-start gap-3 text-left">
              <Mail className="w-5 h-5 text-[color:var(--dms-peach)] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[color:var(--dms-navy)]/80">
                <p className="font-semibold text-[color:var(--dms-navy)]">What happens next</p>
                <p className="mt-1">We'll review your order and start on it right away. If it includes custom work, we'll email you a proof before we print anything.</p>
              </div>
            </div>
            <div className="mt-8 flex gap-3 justify-center">
              {orderNumber && <Link to={`/order/${orderNumber}`} className="btn-ghost-navy">View order</Link>}
              <Link to="/" className="btn-navy">Back to shop <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div className="text-center py-16">
            <h1 className="font-serif text-2xl text-[color:var(--dms-navy)]">We can't confirm this just yet</h1>
            <p className="mt-3 text-[color:var(--dms-navy)]/60 text-sm">If your card was charged, we'll process the order shortly — you'll receive an email either way.</p>
            <Link to="/" className="btn-navy mt-8 inline-flex">Back to shop <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
